import { useEffect, useState } from 'react';
import { X, Settings, Database, Loader2, Ban, CheckCircle2 } from 'lucide-react';
import { fetchPowerPlantParams, savePowerPlantParams, type PowerPlantParam } from '@/api/powerplants';
import toast from 'react-hot-toast';
import { useI18n } from '@/i18n';

interface PlantSettingsModalProps {
  uuid_id: string;
  onClose: () => void;
}

export default function PlantSettingsModal({ uuid_id, onClose }: PlantSettingsModalProps) {
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState('params');
  const [params, setParams] = useState<PowerPlantParam[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    async function loadData() {
      try {
        const data = await fetchPowerPlantParams(uuid_id);
        setParams(data);
      } catch (error) {
        toast.error(t('common.errorLoading'));
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [uuid_id, t]);

  const handleParamChange = (name: string, value: string) => {
    setParams(prev => prev.map(p => p.param_name === name ? { ...p, param_value: value } : p));
  };

  async function handleSave() {
    setSaving(true);
    try {
      await savePowerPlantParams(uuid_id, params);
      toast.success(t('common.saveSuccess'));
      onClose();
    } catch {
      toast.error(t('common.saveError'));
    } finally {
      setSaving(false);
    }
  }

  const getParamLabel = (name: string) => {
    const labels: Record<string, string> = {
      ftp_folder_name: 'FTP Klasör Adı',
      grid_connection_date: 'Şebeke Bağlantı Tarihi',
      contact_mail: 'İletişim E-Posta',
      system_loss: 'Sistem Kaybı (%)',
      installed_power: 'Kurulu Güç (kWp)',
      installed_ac_power: 'AC Kurulu Güç (kW)',
      web_interface: 'Web Arayüz Adresi',
      angle: 'Eğim (derece)',
      aspect: 'Azimut (derece)',
      pvtechchoice: 'Panel Teknolojisi',
      mountingplace: 'Kurulum Tipi (free/building)',
      usehorizon: 'Ufuk Hesabı (1/0)',
    };
    return labels[name] || name.replace(/_/g, ' ').toUpperCase();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30 backdrop-blur-[2px]">
      <div className="relative w-full max-w-2xl bg-white rounded-[16px] shadow-2xl ring-1 ring-black/5 overflow-hidden flex flex-col transform transition-all animate-in fade-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-blue-600">
              <Settings size={20} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Santral Yapılandırması</h3>
              <p className="text-xs text-gray-500">Operasyonel parametreleri yönetin</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-rose-50 hover:text-rose-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs (Underline Style from UIPage) */}
        <div className="w-full bg-white border-b border-gray-100">
          <nav className="flex gap-8 px-6">
            <button
              onClick={() => setActiveTab('params')}
              className={`group relative flex items-center gap-2 py-4 px-1 text-sm font-medium transition-all ${
                activeTab === 'params' ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Database size={18} className={activeTab === 'params' ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'} />
              Parametreler
              {activeTab === 'params' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full shadow-[0_-2px_6px_rgba(37,99,235,0.4)]" />
              )}
            </button>
          </nav>
        </div>

        {/* Body / Content */}
        <div className="p-6 bg-slate-50/30 min-h-[300px] max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="flex h-48 items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                <span className="text-xs font-medium text-gray-400">Veriler yükleniyor...</span>
              </div>
            </div>
          ) : activeTab === 'params' ? (
            <div className="grid gap-5">
              {params.map((p) => (
                <div key={p.param_name} className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-gray-600 uppercase tracking-tight">
                    {getParamLabel(p.param_name)}
                  </label>
                  <div className="relative group">
                    <input
                      type={p.param_name === 'installed_ac_power' ? 'number' : 'text'}
                      min={p.param_name === 'installed_ac_power' ? 0 : undefined}
                      step={p.param_name === 'installed_ac_power' ? 'any' : undefined}
                      value={p.param_value}
                      onChange={(e) => handleParamChange(p.param_name, e.target.value)}
                      placeholder={`${getParamLabel(p.param_name)} değeri girin...`}
                      className="w-full h-11 bg-white border border-gray-200 rounded-lg px-4 text-sm text-gray-800 shadow-sm outline-none transition-all focus:border-blue-500 focus:ring-1 focus:ring-blue-500 group-hover:border-gray-300"
                    />
                  </div>
                </div>
              ))}
              {params.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400 bg-white/50 rounded-xl border-2 border-dashed border-gray-200">
                  <Database size={32} className="opacity-20 mb-2" />
                  <span className="text-sm font-medium italic">Düzenlenebilir parametre bulunamadı</span>
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-4 border-t border-gray-100 bg-gray-50/50 gap-3">
          <button
            onClick={onClose}
            className="flex w-32 items-center justify-center gap-1.5 rounded-lg bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition"
          >
            <Ban size={16} className="text-gray-500" />
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="flex w-32 items-center justify-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 transition disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
            {t('common.save')}
          </button>
        </div>

      </div>
    </div>
  );
}
